import { AuthenticationManager } from '@frontend/authentication-v2';
import { useLocalStorageState } from '@frontend/common';
import { Logger } from '@frontend/logger';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUserType } from '@frontend/transaction/types';
import { PurchaseOrderClient } from '@frontend/ugent/api';
import { PurchaseOrder } from '@frontend/ugent/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    poNumber: string | null;
    setPoNumber: (value: string | null) => void;
    noResolvablePoNumber: boolean;
    setNoResolvablePoNumber: (value: boolean) => void;
    resolvedPurchaseOrder: PurchaseOrder | null;
    setResolvedPurchaseOrder: (value: PurchaseOrder | null) => void;
    resolving: boolean;
    invalidPoNumber: boolean;
    setInvalidPoNumber: (value: boolean) => void;
    spotId: string | null;
    setSpotId: (value: string | null) => void;
    updateWizardData: (data: Record<string, any>) => void;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (type: ScreenType) => void;
}

const useEnterPurchaseOrder = (props: { wizard_id: string }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const { syncTransaction } = useWizardTransaction({ wizardId: props.wizard_id, store });
    const [location] = useLocalStorageState<string | null>('mobile-current-location', null);
    const [poNumber, setPoNumber] = useState<string | null>(
        store?.data.poNumber ||
            (store?.data.transaction?.purchase_order?.startsWith('42')
                ? store?.data.transaction?.purchase_order.substring(2)
                : store?.data.transaction?.purchase_order) ||
            null
    );
    const [noResolvablePoNumber, setNoResolvablePoNumber] = useState<boolean>(
        store?.data.noResolvablePoNumber ||
            (store?.data.transaction?.purchase_order?.startsWith('42') ? false : !!store?.data.transaction?.purchase_order) ||
            false
    );
    const [resolvedPurchaseOrder, setResolvedPurchaseOrder] = useState<PurchaseOrder | null>(store?.data.resolvedPurchaseOrder || null);
    const [resolving, setResolving] = useState<boolean>(false);
    const [invalidPoNumber, setInvalidPoNumber] = useState<boolean>(false);
    const [spotId, setSpotId] = useState<string | null>(store?.data.spotId || store?.data.transaction?.spot_id || null);

    const checkPO = async (accountId: string, poNumber: string) => {
        setResolving(true);
        try {
            setResolvedPurchaseOrder(await PurchaseOrderClient.fetchPurchaseOrder(accountId, '42' + poNumber));
            setInvalidPoNumber(false);
            setResolving(false);
        } catch {
            setInvalidPoNumber(true);
            setResolvedPurchaseOrder(null);
            setResolving(false);
        }
    };

    useEffect(() => {
        const trPO = store?.data.transaction?.purchase_order;
        if (!resolvedPurchaseOrder && trPO && typeof trPO === 'string') {
            if (trPO.startsWith('42')) {
                checkPO(store.wizard.account_id, trPO.substring(2));
            }
        }
    }, []);

    useEffect(() => {
        if (store && props.wizard_id && store.data.transaction) {
            if (!spotId && store.data.transaction.spot_id) setSpotId(store.data.transaction.spot_id);
        }
    }, [store]);

    useEffect(() => {
        if (location && !spotId) {
            setSpotId(location);
        }
    }, [spotId, location]);

    useEffect(() => {
        if (!noResolvablePoNumber && store && poNumber !== null && poNumber.length === 8) {
            checkPO(store.wizard.account_id, poNumber);
        }
    }, [store, noResolvablePoNumber, poNumber]);

    const onSubmit = async () => {
        if (!store || !props.wizard_id) return;
        if (!resolvedPurchaseOrder && !noResolvablePoNumber) return;
        if (!spotId) return;

        const token = await AuthenticationManager.getInstance().getTokenWhenReady();
        const newTransaction = await syncTransaction({
            spot_id: spotId,
            supplier: resolvedPurchaseOrder?.supplier || null,
            purchase_order: resolvedPurchaseOrder?.po_number || poNumber?.toString() || null,
            shipping_notes: resolvedPurchaseOrder?.shipping_notes || null,
            user_id: token?.entity_id || null,
            shipping_address: resolvedPurchaseOrder?.address || null
        });
        let receiver = null;
        if (resolvedPurchaseOrder) {
            receiver = TransactionUserClient.postTransactionUser(store.wizard.account_id, newTransaction.id, {
                user_id: resolvedPurchaseOrder.receiver_id,
                type: TransactionUserType.RECEIVER
            }).catch((e) => {
                Logger.error('Failed to post transaction receiver user', e);
            });
        }

        updateWizardData({
            poNumber,
            resolvedPurchaseOrder,
            noResolvablePoNumber,
            spotId,
            transaction: newTransaction,
            receiver
        });
        const nextStep = store?.steps.find(
            (step) =>
                step.screen === ScreenType.ENTER_PURCHASE_ORDER &&
                (noResolvablePoNumber || !resolvedPurchaseOrder?.supplier ? step.trigger === ScreenTrigger.SUBMIT_FALSE : step.trigger === ScreenTrigger.SUBMIT)
        );
        navigateToNextStep(nextStep);
    };

    return {
        poNumber,
        setPoNumber,
        noResolvablePoNumber,
        setNoResolvablePoNumber,
        resolvedPurchaseOrder,
        setResolvedPurchaseOrder,
        resolving,
        invalidPoNumber,
        setInvalidPoNumber,
        updateWizardData,
        spotId,
        setSpotId,
        submitDisabled: !store?.data.transaction || (!resolvedPurchaseOrder && !noResolvablePoNumber) || !spotId,
        onSubmit,
        onBack
    };
};

export default useEnterPurchaseOrder;
